import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Tabs, TabPanel } from 'tea-component';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import useLanguage from '@op/hooks/useLanguage';
import { singleLanguageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import TranslateSelect from '@/pages/opinion/components/SelectComponents/TranslateSelect';
import PostContentList from '@/pages/opinion/components/Snippets/PostContentList';
import { useHotEventFeedsComments, TContentTab } from '../../../hooks/useHotEventFeedsComments';
import { IDiscussionItem } from '../types';
import './index.scss';
import { LanguageType } from '@op/types/common';
import { DBO_POINT } from '@op/operationLog/buriedPoint';
import LanguageFilterSelect from './components/LanguageFilterSelect';

interface PostContentPanelProps {
  item: IDiscussionItem | null;
  gameInfo: any;
  filterParams: Record<string, any> | undefined;
  channelType: string;
  /** 通知父组件当前活跃 Tab 的滚动加载状态，用于父级 List 组件的 onScrollBottom */
  onScrollStateChange?: (state: { handleScrollBottom: () => void; loadingMore: boolean }) => void;
}

/** 每个 Tab 独立数据加载，通过 useHotEventFeedsComments hook 管理 */
function ContentTabList({
  item,
  gameInfo,
  filterParams,
  tabType,
  channelType,
  language,
  sortBy,
  languageFilter,
  disableScrollLoad,
  onScrollStateChange,
}: {
  item: IDiscussionItem;
  gameInfo: any;
  filterParams: Record<string, any>;
  tabType: TContentTab;
  channelType: string;
  language: LanguageType;
  sortBy?: string;
  languageFilter?: string[];
  /** 是否禁用内部滚动加载 */
  disableScrollLoad?: boolean;
  onScrollStateChange?: (state: { handleScrollBottom: () => void; loadingMore: boolean }) => void;
}) {
  const { items, loading, loadingMore, handleScrollBottom } = useHotEventFeedsComments(
    item,
    gameInfo,
    filterParams,
    tabType,
    channelType,
    sortBy,
    languageFilter
  );

  // 当禁用内部滚动时，将滚动加载状态同步到父组件
  useEffect(() => {
    if (disableScrollLoad) {
      onScrollStateChange?.({ handleScrollBottom, loadingMore });
    }
  }, [disableScrollLoad, handleScrollBottom, loadingMore, onScrollStateChange]);

  return (
    <PostContentList
      items={items}
      language={language}
      loading={loading}
      loadingMore={loadingMore}
      disableScrollLoad={disableScrollLoad}
      onScrollBottom={disableScrollLoad ? undefined : handleScrollBottom}
      height={disableScrollLoad ? undefined : 290}
      oriPointKey="HC_feedOriginUrl"
      deatilPointKey="HC_feedDetail"
    />
  );
}

/** 帖子内容区域：Posts | Comments 双 Tab + 语种筛选 + 排序 + 列表内容（滚动加载由父级 DiscussionDetail 管理） */
export default function PostContentPanel({ item, gameInfo, filterParams, channelType, onScrollStateChange }: PostContentPanelProps) {
  const { formatMessage, formatOptionsMessage, selected: locale } = useLanguage();

  const [language, setLanguage] = useState<LanguageType>('origin');
  const [sortBy, setSortBy] = useState<string>('engagement');
  const [languageFilter, setLanguageFilter] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<TContentTab>(
    () => (item?.post_count === 0 && item?.comment_count > 0) ? 'Comments' : 'Posts'
  );

  const tabOptions = useMemo(
    () => [
      {
        id: 'Posts' as TContentTab,
        label: (
          <span className="dbo-post-content-tab-label">
            <span className="dbo-post-content-tab-icon dbo-post-content-tab-icon--posts" />
            {formatMessage({ id: 'Posts' })} ({item?.post_count || 0})
          </span>
        ),
      },
      {
        id: 'Comments' as TContentTab,
        label: (
          <span className="dbo-post-content-tab-label">
            <span className="dbo-post-content-tab-icon dbo-post-content-tab-icon--comments" />
            {formatMessage({ id: 'Comments-2' })} ({item?.comment_count || 0})
          </span>
        ),
      },
    ],
    [formatMessage],
  );

  const toolbar = (
    <div className="dbo-post-content-toolbar">
      <div className="dbo-post-content-toolbar-left">
        <TranslateSelect
          borderNone={true}
          langOptions={formatOptionsMessage(singleLanguageSelectOptions)}
          lang={language}
          isMatchButtonWidth={false}
          isSingle={true}
          getChange={(e: any) => setLanguage(e)}
        />
        <LanguageFilterSelect
          gameInfo={gameInfo}
          value={languageFilter}
          onChange={setLanguageFilter}
          lang={locale}
        />
      </div>
      <SortBySelect
        borderNone={true}
        defaultValue={sortBy}
        onChange={(_key: string, value: string) => setSortBy(value)}
        defaultParams={{
          options: [
            { value: 'engagement', text: formatMessage({ id: 'Engagement' }) },
            { value: 'tweets_like', text: formatMessage({ id: 'Like' }) },
            { value: 'tweets_reply', text: formatMessage({ id: 'Comment' }) },
            { value: 'view', text: formatMessage({ id: 'View' }) },
          ],
          className: 'dbo-post-content-sort',
          btnTextPrefix: formatMessage({ id: 'Sort By' }),
        }}
      />
    </div>
  );

  const handleTabChange = useCallback((tab: { id: string; }) => {
    DBO_POINT.btn("HC_summaryFeedsChange", tab.id);
    setActiveTab(tab.id as TContentTab);
  }, []);

  // Reset tab when item changes
  useEffect(() => {
    setActiveTab(
      (item?.post_count === 0 && item?.comment_count > 0) ? 'Comments' : 'Posts'
    );
    setLanguageFilter([]);
  }, [item]);

  if (!item || !gameInfo?.edition_unified_id) {
    return null;
  }

  return (
    <div className="dbo-post-content">
      <Tabs
        className="dbo-post-content-tabs"
        tabs={tabOptions}
        animated={false}
        activeId={activeTab}
        onActive={handleTabChange}
      >
        <TabPanel id="Posts">
          {toolbar}
          <ContentTabList
            item={item}
            gameInfo={gameInfo}
            filterParams={filterParams || {}}
            tabType="Posts"
            channelType={channelType}
            language={language}
            sortBy={sortBy}
            languageFilter={languageFilter}
            disableScrollLoad={!!onScrollStateChange}
            onScrollStateChange={activeTab === 'Posts' ? onScrollStateChange : undefined}
          />
        </TabPanel>
        <TabPanel id="Comments">
          {toolbar}
          <ContentTabList
            item={item}
            gameInfo={gameInfo}
            filterParams={filterParams || {}}
            tabType="Comments"
            channelType={channelType}
            language={language}
            sortBy={sortBy}
            languageFilter={languageFilter}
            disableScrollLoad={!!onScrollStateChange}
            onScrollStateChange={activeTab === 'Comments' ? onScrollStateChange : undefined}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
}
